import { useState } from 'react';
import { Alert, Button } from '@mui/material';
import LaunchLink from '../../components/plaid/LinkLauncher';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLinkToken, setIncomeSuccessful, recordPlaidLinkSession } from '../../services/plaid.service';
import { updatePlaidUserInfo } from '../../store/ui/actions';
import { getPlaidUserInfo, getUserDetails } from '../../store/ui/selector';
import { ProofButton } from '../../ui/button/ProofButton';
import { useTranslation } from 'react-i18next';
import { IncomeRequest } from '../../services/plaid.types';
import { useLocation, useNavigate, useParams } from 'react-router';

export enum IncomeType {
  Bank = 'bank',
  Payroll = 'payroll',
}

interface Props {
  income: boolean;
  incomeType: IncomeType;
  buttonText: string;
  variant?: 'contained' | 'text' | 'outlined';
  startIcon?: React.ReactNode;
}

/**
 * Grabs a link token from the server, calls Link, and then reports success back to the server
 * (Plaid Income: the server fetches the income itself).
 */

const LinkLoader = ({ income, incomeType, buttonText, variant = 'contained', startIcon }: Props) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plaidUserInfo = useSelector(getPlaidUserInfo);
  const userDetails = useSelector(getUserDetails);
  let { propertyCode } = useParams();
  const [linkToken, setLinkToken] = useState('');
  const [errorKey, setErrorKey] = useState('');
  // Set when Link itself succeeded but the API could not record the income: "Try again" repeats
  // that call without making the user link their account a second time.
  const [failedSuccess, setFailedSuccess] = useState<{ public_token: string; linkSessionId?: string } | null>(null);
  const [retrying, setRetrying] = useState(false);
  // plaidUserInfo is filled on the account step and can be empty mid-flow (a refresh on /types
  // or /upload, or the first load after store_v2 was retired); fall back to the applicant the
  // link itself loaded.
  const applicantId = plaidUserInfo?.applicant_id || userDetails?.applicant_id;
  const email = plaidUserInfo?.email || userDetails?.email || '';
  const { t, i18n } = useTranslation();

  const loadAndLaunchLink = async () => {
    setErrorKey('');
    setFailedSuccess(null);
    const linkdPayload: IncomeRequest = {
      income,
      incomeType,
      // Ignored by the new API (it sets this itself); the one in production still needs it.
      // See IncomeRequest.client_user_id — remove with it.
      client_user_id: `plaid_${process.env.REACT_APP_APP_ENV !== 'prod' ? 'dev' : 'prod'}_${applicantId || propertyCode}`,
      email,
      applicantId,
      lang: i18n.language,
    };
    if (incomeType === IncomeType.Payroll) {
      // Digital (linked) payroll. Keep sending it: with no `flow` the API falls back to document income.
      linkdPayload.flow = 'link';
    }
    const tokenResponse = await fetchLinkToken(linkdPayload);
    if (tokenResponse?.status === 200 && tokenResponse.data?.link_token) {
      setLinkToken(tokenResponse.data.link_token);
    } else {
      setErrorKey('verification.type.plaid.linkTokenError');
    }
  };

  const linkSuccess = async (public_token: string, linkSessionId?: string) => {
    if (public_token) {
      await incomeSuccess(public_token, linkSessionId);
    }
  };

  const incomeSuccess = async (public_token: string, linkSessionId?: string) => {
    const response = await setIncomeSuccessful({ email, incomeType, public_token, applicantId, linkSessionId });
    // setIncomeSuccessful resolves undefined when the call fails. This used to read
    // `response.status` unguarded and throw, leaving the user stuck after a successful Link.
    // Exactly `true` (or 'success'): a `{status: 'fail'}` body is truthy and must never reach
    // the confirmation page.
    const succeeded = response?.status === true || response?.status === 'success';
    if (succeeded) {
      setFailedSuccess(null);
      dispatch(
        updatePlaidUserInfo({
          income_status: true,
        })
      );
      // `search`, not `?token=${token}`: carries the link token as every other step does, and
      // can't turn a missing one into `token=null`.
      navigate(`/${propertyCode}/verification/confirmation${search}`);
    } else {
      setFailedSuccess({ public_token, linkSessionId });
      setErrorKey('verification.type.plaid.incomeError');
    }
  };

  const retryIncomeSuccess = async () => {
    if (!failedSuccess) return;
    setRetrying(true);
    try {
      await incomeSuccess(failedSuccess.public_token, failedSuccess.linkSessionId);
    } finally {
      setRetrying(false);
    }
  };

  const handleLinkExit = (error: any, metadata: any) => {
    try {
      recordPlaidLinkSession({
        applicantId,
        sessionEvent: {
          event: 'exit',
          attemptType: incomeType,
          exitStatus: metadata?.status ?? null,
          institutionName: metadata?.institution?.name ?? null,
          institutionId: metadata?.institution?.institution_id ?? null,
          errorType: error?.error_type ?? null,
          errorCode: error?.error_code ?? null,
          errorMessage: error?.error_message ?? null,
          linkSessionId: metadata?.link_session_id ?? null,
          requestId: metadata?.request_id ?? null,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (e) {
      console.log('handleLinkExit error (non-fatal)', e);
    }
  };

  const handleLinkSuccess = (metadata: any) => {
    try {
      recordPlaidLinkSession({
        applicantId,
        sessionEvent: {
          event: 'success',
          attemptType: incomeType,
          exitStatus: null,
          institutionName: metadata?.institution?.name ?? null,
          institutionId: metadata?.institution?.institution_id ?? null,
          errorType: null,
          errorCode: null,
          errorMessage: null,
          linkSessionId: metadata?.link_session_id ?? null,
          requestId: null,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (e) {
      console.log('handleLinkSuccess error (non-fatal)', e);
    }
  };

  return (
    <>
      <ProofButton color="primary" variant={variant} fullWidth sx={{ mb: 2, py: 1 }} startIcon={startIcon} onClick={() => loadAndLaunchLink()}>
        {buttonText}
      </ProofButton>
      {errorKey && (
        <Alert
          severity="error"
          sx={{ mb: 2, textAlign: 'left' }}
          action={
            failedSuccess ? (
              <Button color="inherit" size="small" disabled={retrying} onClick={retryIncomeSuccess}>
                {t('page.tryAgain')}
              </Button>
            ) : undefined
          }
        >
          {t(errorKey)}
        </Alert>
      )}
      {linkToken && (
        <LaunchLink token={linkToken} successCallback={linkSuccess} exitCallback={handleLinkExit} successMetadataCallback={handleLinkSuccess} />
      )}
    </>
  );
};

export default LinkLoader;
